/**
 * usePresets Hook
 *
 * Manages preset state (Draft/Active/Expired chips) driven by metadata.
 * Presets are quick-filter buttons configured in YAML _meta.presets.
 *
 * Features:
 * - Default preset initialization from metadata
 * - Coexistence with Advanced Filters (last touch wins)
 * - Filter building for API from selected preset
 * - Toolbar integration props
 *
 * @example
 * ```tsx
 * const { presets } = useMetadataColumns('offering')
 * const {
 *   selectedPreset,
 *   handlePresetChange,
 *   presetFilters,
 *   toolbarPresetProps
 * } = usePresets({
 *   presets,
 *   advancedFiltersActive: advancedFilterCount > 0,
 *   onClearAdvancedFilters: () => setAdvancedFilterRows([]),
 *   onPageReset: () => setCurrentPage(1)
 * })
 *
 * // Use in toolbar
 * <MetadataTableToolbar {...toolbarPresetProps} />
 *
 * // Combine filters for API
 * const apiFilters = [...baseFilters, ...presetFilters, ...advancedFilters]
 * ```
 */

import { useState, useMemo, useEffect } from 'react'
import type { Filter } from '@/types/api.types'
import type { PresetsMetadata } from '@/services/metadataService'

export interface UsePresetsOptions {
  /** Presets metadata from YAML (from useMetadataColumns) */
  presets?: PresetsMetadata

  /** Whether Advanced Filters are active (for coexistence) */
  advancedFiltersActive?: boolean

  /** Callback to clear Advanced Filters when preset selected */
  onClearAdvancedFilters?: () => void

  /** Callback when page should reset */
  onPageReset?: () => void
}

export interface UsePresetsResult {
  /** Currently selected preset key (e.g., 'draft', 'active', 'all') */
  selectedPreset: string | null

  /** Handler for preset change (pass to toolbar) */
  handlePresetChange: (presetKey: string) => void

  /** Filters to apply to API based on selected preset */
  presetFilters: Filter[]

  /** Props to pass to MetadataTableToolbar */
  toolbarPresetProps: {
    presets?: PresetsMetadata['options']
    activePreset: string | null
    onPresetChange: (key: string) => void
  }
}

/**
 * Hook to manage preset state and filter building.
 *
 * @param options - Configuration including presets metadata and coexistence callbacks
 * @returns Preset state, handlers, filters, and toolbar props
 */
export function usePresets(options: UsePresetsOptions = {}): UsePresetsResult {
  const {
    presets,
    advancedFiltersActive = false,
    onClearAdvancedFilters,
    onPageReset,
  } = options

  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)

  // Initialize with default preset on mount (once when presets available)
  // Fallback: if no default specified, use first preset option
  // This ensures something is always selected since Segmented doesn't support deselection
  // CRITICAL: Do NOT initialize if Advanced Filters are active (prevents infinite loop)
  useEffect(() => {
    if (presets && presets.options?.length > 0 && selectedPreset === null && !advancedFiltersActive) {
      const defaultKey = presets.default ?? presets.options[0].key
      setSelectedPreset(defaultKey)
    }
  }, [presets, selectedPreset, advancedFiltersActive])

  // Coexistence: If Advanced Filters becomes active, clear preset
  useEffect(() => {
    if (advancedFiltersActive && selectedPreset) {
      setSelectedPreset(null)
    }
  }, [advancedFiltersActive, selectedPreset])

  // Build filter array from selected preset
  const presetFilters = useMemo<Filter[]>(() => {
    // "All" preset or no preset selected = no filter applied
    if (!selectedPreset || !presets || selectedPreset === 'all') {
      return []
    }

    const preset = presets.options.find((p) => p.key === selectedPreset)
    if (!preset || !preset.filter) {
      // Preset not found or has null filter = no filter applied
      return []
    }

    // Build filter from preset definition
    const filter: Filter = {
      key: presets.field, // Use the field specified in presets metadata
      constraint_type: preset.filter.constraint_type as any, // PresetFilter uses string, Filter uses ConstraintType enum
    }

    // Add value/values/value2 based on operator requirements
    if (preset.filter.value !== undefined) {
      filter.value = preset.filter.value
    }
    if (preset.filter.values !== undefined) {
      filter.values = preset.filter.values
    }
    if (preset.filter.value2 !== undefined) {
      // @ts-expect-error - value2 not in Filter type but exists in PresetFilter
      filter.value2 = preset.filter.value2
    }

    return [filter]
  }, [selectedPreset, presets])

  // Handle preset change
  const handlePresetChange = (presetKey: string) => {
    setSelectedPreset(presetKey)

    // Reset to page 1 when filter changes
    onPageReset?.()

    // Clear Advanced Filters (preset takes precedence - last touch wins)
    onClearAdvancedFilters?.()
  }

  // Toolbar props
  const toolbarPresetProps = {
    presets: presets?.options,
    activePreset: selectedPreset,
    onPresetChange: handlePresetChange,
  }

  return {
    selectedPreset,
    handlePresetChange,
    presetFilters,
    toolbarPresetProps,
  }
}
